import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { createWorker } from 'tesseract.js';
import Card from '../../components/Card';
import { useLanguage } from '../../i18n/LanguageContext';
import apiUrl from '../../config/api';

const normalizeText = (value = '') => String(value)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^\p{L}\p{N}\s]/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getWords = (value) => normalizeText(value).split(' ').filter((word) => word.length > 2);
const getAnswerText = (answer) => String(answer?.text || '').trim();

const scoreQuestion = (question, recognizedText) => {
  const normalizedFrame = normalizeText(recognizedText);
  const questionWords = [...new Set(getWords(question.questionText))];

  if (!normalizedFrame || questionWords.length < 2) {
    return 0;
  }

  const matchingWords = questionWords.filter((word) => normalizedFrame.includes(word)).length;
  const questionCoverage = matchingWords / questionWords.length;
  const exactBonus = normalizedFrame.includes(question.normalizedQuestionText) ? 0.35 : 0;
  const answerWords = [...new Set((question.answers || []).flatMap((answer) => getWords(getAnswerText(answer))))];
  const answerCoverage = answerWords.length
    ? answerWords.filter((word) => normalizedFrame.includes(word)).length / answerWords.length
    : 0;

  return Math.min(1, (questionCoverage * 0.78) + (answerCoverage * 0.22) + exactBonus);
};

const ScannerLive = () => {
  const { t } = useLanguage();
  const text = t.scannerLivePage;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const workerRef = useRef(null);
  const timerRef = useRef(null);
  const scanningRef = useRef(false);
  const [archives, setArchives] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [selectedArchiveId, setSelectedArchiveId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [questionType, setQuestionType] = useState('both');
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [liveMatches, setLiveMatches] = useState([]);
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    Promise.all([
      axios.post(`${apiUrl}/getArchives`, {}, { headers }),
      axios.post(`${apiUrl}/scanner/questions`, {}, { headers }),
    ]).then(([archiveResponse, questionResponse]) => {
      setArchives(archiveResponse.data || []);
      setQuestions((questionResponse.data || []).map((question) => ({
        ...question,
        normalizedQuestionText: normalizeText(question.questionText),
      })));
    }).catch((requestError) => {
      setError(requestError.response?.data?.message || requestError.message);
    }).finally(() => setIsLoading(false));
  }, [headers, token]);

  const availableSubjects = useMemo(() => {
    const byId = new Map();
    questions.forEach((question) => {
      if ((!selectedArchiveId || question.archiveId === selectedArchiveId) && question.subjectId) {
        byId.set(question.subjectId, question.subjectName);
      }
    });
    return [...byId.entries()].map(([id, name]) => ({ id, name }));
  }, [questions, selectedArchiveId]);

  useEffect(() => {
    if (selectedSubjectId && !availableSubjects.some((subject) => subject.id === selectedSubjectId)) {
      setSelectedSubjectId('');
    }
  }, [availableSubjects, selectedSubjectId]);

  const searchableQuestions = useMemo(() => questions.filter((question) => (
    (!selectedArchiveId || question.archiveId === selectedArchiveId)
    && (!selectedSubjectId || question.subjectId === selectedSubjectId)
    && (questionType === 'both' || question.questionType === questionType)
  )), [questionType, questions, selectedArchiveId, selectedSubjectId]);

  const stopLiveScanner = useCallback(async () => {
    scanningRef.current = false;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (workerRef.current) {
      await workerRef.current.terminate().catch(() => undefined);
      workerRef.current = null;
    }
    setIsLive(false);
    setIsAnalyzing(false);
  }, []);

  useEffect(() => () => {
    scanningRef.current = false;
    clearTimeout(timerRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    workerRef.current?.terminate().catch(() => undefined);
  }, []);

  const analyzeFrame = useCallback(async () => {
    if (!scanningRef.current || !videoRef.current || !canvasRef.current || !workerRef.current) {
      return;
    }

    const video = videoRef.current;
    if (!video.videoWidth) {
      timerRef.current = setTimeout(analyzeFrame, 800);
      return;
    }

    setIsAnalyzing(true);
    const canvas = canvasRef.current;
    const maxWidth = 1800;
    const scale = Math.min(1, maxWidth / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.filter = 'grayscale(1) contrast(1.35) brightness(1.08)';
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      const result = await workerRef.current.recognize(canvas);
      if (!scanningRef.current) return;
      const recognizedText = result.data?.text || '';
      const matches = searchableQuestions
        .map((question) => ({ ...question, confidence: Math.round(scoreQuestion(question, recognizedText) * 100) }))
        .filter((question) => question.confidence >= 43)
        .sort((first, second) => second.confidence - first.confidence)
        .slice(0, 4);

      setLiveMatches(matches);
      if (matches.length) {
        setSessionQuestions((previous) => {
          const next = [...previous];
          matches.forEach((match) => {
            const existingIndex = next.findIndex((question) => question._id === match._id);
            const entry = { ...match, detectedAt: new Date().toISOString() };
            if (existingIndex >= 0) next[existingIndex] = entry;
            else next.push(entry);
          });
          return next;
        });
      }
    } catch (ocrError) {
      if (scanningRef.current) setError(text.errors.ocr);
    } finally {
      setIsAnalyzing(false);
      if (scanningRef.current) timerRef.current = setTimeout(analyzeFrame, 2200);
    }
  }, [searchableQuestions, text.errors.ocr]);

  const startLiveScanner = async () => {
    setError('');
    setIsStarting(true);
    setLiveMatches([]);
    setSessionQuestions([]);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } },
        audio: false,
      });
      const [videoTrack] = stream.getVideoTracks();
      const capabilities = videoTrack?.getCapabilities?.() || {};
      const advancedConstraints = {};
      if (capabilities.focusMode?.includes?.('continuous')) advancedConstraints.focusMode = 'continuous';
      if (capabilities.exposureMode?.includes?.('continuous')) advancedConstraints.exposureMode = 'continuous';
      if (capabilities.whiteBalanceMode?.includes?.('continuous')) advancedConstraints.whiteBalanceMode = 'continuous';
      if (Object.keys(advancedConstraints).length) {
        await videoTrack.applyConstraints({ advanced: [advancedConstraints] }).catch(() => undefined);
      }
      streamRef.current = stream;
      setIsLive(true);
      scanningRef.current = true;
      workerRef.current = await createWorker('ita+eng');
      await workerRef.current.setParameters({
        tessedit_pageseg_mode: '11',
        preserve_interword_spaces: '1',
        user_defined_dpi: '300',
      });
      setTimeout(() => {
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        videoRef.current.play().then(analyzeFrame).catch(() => setError(text.errors.camera));
      }, 0);
    } catch (cameraError) {
      await stopLiveScanner();
      setError(text.errors.camera);
    } finally {
      setIsStarting(false);
    }
  };

  const renderAnswer = (question) => {
    const correctAnswers = (question.answers || []).filter((answer) => answer.isCorrect).map(getAnswerText).filter(Boolean);
    if (!correctAnswers.length) return text.noAnswer;
    return correctAnswers.join(' / ');
  };

  return (
    <>
      <Row><Col lg="12">
        <Card className="scanner-live-card" data-aos="fade-up"><Card.Body>
          <div className="scanner-live-heading">
            <div><h3>{text.title}</h3><p>{text.subtitle}</p></div>
            <Badge bg="primary">{text.questionsAvailable(searchableQuestions.length)}</Badge>
          </div>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          <div className="scanner-live-settings">
            <Form.Group><Form.Label>{text.archive}</Form.Label><Form.Select value={selectedArchiveId} onChange={(event) => setSelectedArchiveId(event.target.value)}><option value="">{text.allArchives}</option>{archives.map((archive) => <option key={archive._id} value={archive._id}>{archive.name}</option>)}</Form.Select></Form.Group>
            <Form.Group><Form.Label>{text.subject}</Form.Label><Form.Select value={selectedSubjectId} onChange={(event) => setSelectedSubjectId(event.target.value)}><option value="">{text.allSubjects}</option>{availableSubjects.map((subject) => <option key={subject.id} value={subject.id}>{subject.name}</option>)}</Form.Select></Form.Group>
            <Form.Group><Form.Label>{text.questionType}</Form.Label><Form.Select value={questionType} onChange={(event) => setQuestionType(event.target.value)}><option value="both">{text.both}</option><option value="closed">{text.closed}</option><option value="open">{text.open}</option></Form.Select></Form.Group>
          </div>
          <Button variant="primary" className="scanner-live-start" onClick={startLiveScanner} disabled={isLoading || isStarting || !searchableQuestions.length}>
            {isStarting ? <><Spinner size="sm" animation="border" /> {text.starting}</> : text.start}
          </Button>
          {sessionQuestions.length > 0 && !isLive && <div className="scanner-live-summary"><h5>{text.summary}</h5>{sessionQuestions.map((question, index) => <div className="scanner-live-summary-item" key={question._id}><span>{index + 1}</span><div><strong>{question.questionText}</strong><small>{question.subjectName}</small><p>{renderAnswer(question)}</p></div></div>)}</div>}
        </Card.Body></Card>
      </Col></Row>

      {isLive && <div className="scanner-live-hud">
        <video ref={videoRef} className="scanner-live-video" playsInline muted />
        <canvas ref={canvasRef} className="d-none" />
        <div className="scanner-live-topbar"><div><span className={`scanner-live-status ${isAnalyzing ? 'is-scanning' : ''}`} />{isAnalyzing ? text.analyzing : text.live}</div><Button variant="danger" onClick={stopLiveScanner}>{text.stop}</Button></div>
        <div className="scanner-live-reticle" aria-hidden="true"><span /><span /><span /><span /></div>
        <div className="scanner-live-results">
          {liveMatches.length ? liveMatches.map((question) => <div className="scanner-live-result" key={question._id}><div className="scanner-live-result-meta"><Badge bg={question.questionType === 'open' ? 'warning' : 'primary'}>{question.questionType === 'open' ? text.open : text.closed}</Badge><span>{question.confidence}%</span></div><strong>{question.questionText}</strong><small>{question.subjectName}</small><p>{renderAnswer(question)}</p></div>) : <div className="scanner-live-waiting">{isAnalyzing ? text.reading : text.pointCamera}</div>}
        </div>
        <div className="scanner-live-counter">{text.detected(sessionQuestions.length)}</div>
      </div>}
    </>
  );
};

export default ScannerLive;
